import { Link } from 'react-router-dom';
import { Linkedin, Youtube, Instagram, ArrowUpRight, ArrowRight } from 'lucide-react';
import { LOGOS } from '@/lib/constants';
import { useState } from 'react';
import { subscribeNewsletter } from '@/lib/api';

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    try {
      await subscribeNewsletter(email);
      setSubscribed(true);
      setEmail('');
    } catch {
      // Silently handle
    }
  };

  return (
    <footer className="bg-[#1A1008] text-white relative" data-testid="main-footer">
      <div className="noise-overlay absolute inset-0 pointer-events-none" />
      
      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
        {/* Top section */}
        <div className="py-16 lg:py-20 border-b border-white/6">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8">
            {/* Brand + Newsletter */}
            <div className="lg:col-span-5">
              <Link to="/">
                <img src={LOGOS.whiteColored} alt="Anexi Consulting" className="h-10 w-auto mb-6" />
              </Link>
              <p className="text-[14px] text-white/40 leading-[1.7] max-w-sm mb-8">
                Transforming organisations from the inside out since 2007. Strategy. Learning. Experience. Research. AI. Across 739+ organisations.
              </p>

              {/* Newsletter */}
              <p className="text-[12px] font-medium text-white/50 tracking-[0.1em] uppercase mb-3">Stay Updated</p>
              {subscribed ? (
                <p className="text-[14px] text-[#F59E0B]">Subscribed. Thank you.</p>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 bg-white/5 border border-white/8 rounded-full px-4 py-2.5 text-[13px] text-white placeholder:text-white/20 focus:outline-none focus:border-white/20 transition-colors max-w-[240px]"
                    required
                    data-testid="newsletter-email-input"
                  />
                  <button
                    type="submit"
                    className="bg-white/10 hover:bg-white/15 text-white p-2.5 rounded-full transition-colors"
                    data-testid="newsletter-submit-button"
                    aria-label="Subscribe"
                  >
                    <ArrowRight size={14} />
                  </button>
                </form>
              )}
            </div>

            {/* Links */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
                <div>
                  <h4 className="text-[12px] font-medium text-white/30 tracking-[0.15em] uppercase mb-5">Services</h4>
                  <ul className="space-y-3">
                    {[
                      { label: 'Consulting', href: '/consulting' },
                      { label: 'Learning & Development', href: '/learning' },
                      { label: 'Executive Search', href: '/executive-search' },
                      { label: 'Assessments', href: '/assessments' },
                      { label: 'Engagement & Research', href: '/engagement' },
                      { label: 'BOTS.AI', href: '/botsai' },
                    ].map(link => (
                      <li key={link.href}>
                        <Link to={link.href} className="text-[13px] text-white/40 hover:text-white/70 transition-colors">{link.label}</Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-[12px] font-medium text-white/30 tracking-[0.15em] uppercase mb-5">Company</h4>
                  <ul className="space-y-3">
                    {[
                      { label: 'About Us', href: '/about' },
                      { label: 'Our Impact', href: '/impact' },
                      { label: 'Our New Chapter', href: '/our-new-chapter' },
                      { label: 'Contact', href: '/contact' },
                    ].map(link => (
                      <li key={link.href}>
                        <Link to={link.href} className="text-[13px] text-white/40 hover:text-white/70 transition-colors">{link.label}</Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-[12px] font-medium text-white/30 tracking-[0.15em] uppercase mb-5">Get in Touch</h4>
                  <div className="space-y-3 text-[13px]">
                    <p className="text-white/40">Mumbai, India</p>
                    <a href="tel:+919876543210" className="text-white/40 hover:text-white/70 transition-colors block">+91 98765 43210</a>
                    <a href="mailto:hello@anexiconsulting.com" className="text-white/40 hover:text-white/70 transition-colors block">hello@anexiconsulting.com</a>
                  </div>
                  <div className="flex gap-2 mt-6">
                    {[
                      { href: 'https://linkedin.com', icon: Linkedin, label: 'LinkedIn' },
                      { href: 'https://youtube.com', icon: Youtube, label: 'YouTube' },
                      { href: 'https://instagram.com', icon: Instagram, label: 'Instagram' },
                    ].map(social => (
                      <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors" aria-label={social.label}>
                        <social.icon size={14} className="text-white/40" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="py-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-[12px] text-white/20">
            2026 Anexi Consulting. Formerly HR Anexi.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-[12px] text-white/20 hover:text-white/40 transition-colors">Privacy</Link>
            <Link to="/terms" className="text-[12px] text-white/20 hover:text-white/40 transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
